import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { AppHeader } from "@/components/AppHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Calendar,
  CalendarOff,
  ChevronLeft,
  ChevronRight,
  Clock,
  ZoomIn,
  Phone,
  Plus,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { AGENDA_DAYS, DEFAULT_OPENING_HOURS, bookingHoursOf, normalizeOpeningHours, findAbsence, absencesDuJour, libelleAbsence } from "@/lib/utils";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Niveaux de zoom : pas du clic et hauteur d'une case (plus fin = journée plus haute)
const ZOOMS = [
  { pas: 30, hauteur: 36, label: "30 min" },
  { pas: 15, hauteur: 24, label: "15 min" },
  { pas: 5, hauteur: 12, label: "5 min" },
];
// Les plages sont toujours mémorisées à 5 min près, quel que soit le zoom : sinon une
// plage tracée au quart d'heure serait perdue en la rééditant en zoom 30 min.
const RESOLUTION_PLAGES = 5;
// En mode « disponibilités », la grille couvre large pour pouvoir étendre les plages
const DISPO_START = 7 * 60;
const DISPO_END = 20 * 60;

const toMinutes = (hhmm) => {
  const [h, m] = (hhmm || "00:00").split(":").map(Number);
  return h * 60 + (m || 0);
};
const pad = (n) => String(n).padStart(2, "0");
const toHHMM = (minutes) => `${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`;
const toISODate = (d) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const mondayOf = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const shift = (d.getDay() + 6) % 7; // lundi = 0
  d.setDate(d.getDate() - shift);
  return d;
};
const addDays = (date, n) => {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
};
const addMonths = (date, n) => {
  const d = new Date(date);
  d.setDate(1);
  d.setMonth(d.getMonth() + n);
  return d;
};

// Pré-remplissage depuis le texte capté par l'IA ("22/07/2026", "14:30", "14 heures 30")
const parseDateText = (s) => {
  const m = /(\d{1,2})\/(\d{1,2})\/(\d{4})/.exec(s || "");
  return m ? `${m[3]}-${pad(m[2])}-${pad(m[1])}` : "";
};
const parseTimeText = (s) => {
  const m = /(\d{1,2})\s*(?:h(?:eures?)?|:)\s*(\d{2})?/i.exec(s || "");
  return m ? `${pad(m[1])}:${m[2] || "00"}` : "";
};

// Codes couleur des zones de l'agenda
const ZONE_STYLES = {
  rdv: "bg-emerald-50 hover:bg-emerald-100 cursor-pointer",
  ouvert: "bg-sky-50 hover:bg-sky-100 cursor-pointer",
  ferme: "bg-muted/60",
  absence: "bg-red-100/70",
};
// Hachures des absences : lisibles d'un coup d'oeil, meme en vue mois
const HACHURES_ABSENCE = {
  backgroundImage:
    "repeating-linear-gradient(135deg, rgba(220,38,38,0.12) 0 6px, transparent 6px 12px)",
};

const VIEWS = [
  { key: "jour", label: "Jour" },
  { key: "semaine", label: "Semaine" },
  { key: "mois", label: "Mois" },
];

// --- Conversion plages <-> cases de 30 min, pour l'édition à la souris ---

const rangesVersCases = (ranges, debut, fin) => {
  const n = (fin - debut) / RESOLUTION_PLAGES;
  const cases = new Array(n).fill(false);
  (ranges || []).forEach((r) => {
    const d = Math.max(toMinutes(r.start), debut);
    const f = Math.min(toMinutes(r.end), fin);
    for (let m = d; m < f; m += RESOLUTION_PLAGES) {
      const i = (m - debut) / RESOLUTION_PLAGES;
      if (i >= 0 && i < n) cases[i] = true;
    }
  });
  return cases;
};

const casesVersRanges = (cases, debut) => {
  const ranges = [];
  let depart = null;
  cases.forEach((ouvert, i) => {
    const minute = debut + i * RESOLUTION_PLAGES;
    if (ouvert && depart === null) depart = minute;
    if (!ouvert && depart !== null) {
      ranges.push({ start: toHHMM(depart), end: toHHMM(minute) });
      depart = null;
    }
  });
  if (depart !== null) {
    ranges.push({ start: toHHMM(depart), end: toHHMM(debut + cases.length * RESOLUTION_PLAGES) });
  }
  return ranges;
};

const Agenda = () => {
  const [appointments, setAppointments] = useState([]);
  const [hours, setHours] = useState(DEFAULT_OPENING_HOURS); // plages de RDV Fruitycall
  const [cabinetHours, setCabinetHours] = useState(DEFAULT_OPENING_HOURS); // horaires officiels
  const [absences, setAbsences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(
    () => localStorage.getItem("fruitycall_agenda_view") || "semaine"
  );
  const [anchor, setAnchor] = useState(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  });
  // Dialogue de planification / édition : { apt, date, time, duration, nom, telephone, motif, isNew }
  const [slotDialog, setSlotDialog] = useState(null);
  const [absencesDialog, setAbsencesDialog] = useState(false);
  const [absenceForm, setAbsenceForm] = useState({ start_date: "", end_date: "", start_time: "", end_time: "", reason: "" });
  // Glisser-déposer et redimensionnement
  const [dropPreview, setDropPreview] = useState(null); // { iso, minutes, duration }
  const dragRef = useRef(null); // { apt, grabOffsetPx }
  const [resizing, setResizing] = useState(null); // { apt, startY, baseDuration, preview }
  // Édition des plages d'ouverture à la souris
  const [modeDispo, setModeDispo] = useState(false);
  const [dispoModifiee, setDispoModifiee] = useState(false);
  const [tracage, setTracage] = useState(null); // { dayKey, debut, fin, ouvrir }
  // Zoom : précision du clic sur la grille (mémorisé entre les visites)
  const [zoom, setZoom] = useState(() => {
    try {
      const v = Number(localStorage.getItem("fruitycall_agenda_zoom"));
      return ZOOMS.some((z) => z.pas === v) ? v : 30;
    } catch {
      return 30;
    }
  });
  const changeZoom = (pas) => {
    setZoom(pas);
    try {
      localStorage.setItem("fruitycall_agenda_zoom", String(pas));
    } catch {
      /* stockage indisponible : le zoom reste valable pour la session */
    }
  };
  const { pas: SLOT_MINUTES, hauteur: SLOT_HEIGHT } = ZOOMS.find((z) => z.pas === zoom);
  const SNAP_MINUTES = SLOT_MINUTES; // déplacement et redimensionnement suivent le zoom
  // Tracé d'une absence à la souris : { iso, debut, fin } pendant le glissement
  const [modeAbsence, setModeAbsence] = useState(false);
  const [traceAbsence, setTraceAbsence] = useState(null);

  const userEmail = useMemo(
    () => (JSON.parse(localStorage.getItem("fruitycall_user") || "{}").email || ""),
    []
  );

  const changeView = (v) => {
    setView(v);
    localStorage.setItem("fruitycall_agenda_view", v);
  };

  const loadAll = useCallback(async (background = false) => {
    if (!userEmail) {
      setLoading(false);
      return;
    }
    if (!background) setLoading(true);
    try {
      const [aptRes, setRes] = await Promise.all([
        fetch(`${API_URL}/api/appointments?user_email=${encodeURIComponent(userEmail)}`),
        fetch(`${API_URL}/api/voice/settings?user_email=${encodeURIComponent(userEmail)}`),
      ]);
      const aptData = await aptRes.json();
      setAppointments(aptData.appointments || []);
      const settings = await setRes.json();
      setHours(bookingHoursOf(settings));
      setCabinetHours(normalizeOpeningHours(settings.opening_hours));
      setAbsences(settings.absences || []);
    } catch (error) {
      console.error("Erreur chargement agenda:", error);
      if (!background) toast.error("Impossible de charger l'agenda");
    } finally {
      if (!background) setLoading(false);
    }
  }, [userEmail]);

  useEffect(() => {
    loadAll();
    const interval = setInterval(() => loadAll(true), 60000);
    return () => clearInterval(interval);
  }, [loadAll]);

  const persistSettings = async (payload, successMessage) => {
    try {
      const response = await fetch(
        `${API_URL}/api/voice/settings?user_email=${encodeURIComponent(userEmail)}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );
      if (!response.ok) throw new Error();
      if (successMessage) toast.success(successMessage);
      return true;
    } catch {
      toast.error("Erreur lors de l'enregistrement");
      return false;
    }
  };

  const patchAppointment = async (id, payload, successMessage) => {
    try {
      const response = await fetch(`${API_URL}/api/appointments/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error();
      if (successMessage) toast.success(successMessage);
      loadAll(true);
      return true;
    } catch {
      toast.error("Erreur lors de la mise à jour");
      return false;
    }
  };

  // Bornes verticales de la grille : englobe toutes les plages ouvertes (défaut 8h-19h).
  // En mode « disponibilités », on affiche large pour pouvoir étendre les plages.
  const [gridStart, gridEnd] = useMemo(() => {
    if (modeDispo) return [DISPO_START, DISPO_END];
    const ranges = AGENDA_DAYS.flatMap(({ key }) => [
      ...(hours[key]?.open ? hours[key].ranges : []),
      ...(cabinetHours[key]?.open ? cabinetHours[key].ranges : []),
    ]);
    if (ranges.length === 0) return [8 * 60, 19 * 60];
    const start = Math.min(...ranges.map((r) => toMinutes(r.start)));
    const end = Math.max(...ranges.map((r) => toMinutes(r.end)));
    return [Math.floor(start / 60) * 60, Math.ceil(end / 60) * 60];
  }, [hours, cabinetHours, modeDispo]);

  const nSlots = Math.max(1, (gridEnd - gridStart) / SLOT_MINUTES);
  const todayISO = toISODate(new Date());

  const scheduled = appointments.filter(
    (a) => a.scheduled_date && a.scheduled_time && a.status !== "annule"
  );
  const pending = appointments.filter(
    (a) => a.status === "a_confirmer" && !(a.scheduled_date && a.scheduled_time)
  );

  const dayKeyOf = (date) => AGENDA_DAYS[(date.getDay() + 6) % 7].key;

  // Heure pleine marquée, quarts d'heure discrets, pas de 5 min à peine visibles
  const classeLigne = (minutes) =>
    minutes % 60 === 0
      ? "border-border"
      : minutes % 15 === 0
      ? "border-border/40"
      : "border-border/10";

  const dansPlages = (conf, minutes) =>
    !!conf?.open &&
    conf.ranges.some((r) => minutes >= toMinutes(r.start) && minutes < toMinutes(r.end));

  // "absence" | "rdv" (plage proposee par la secretaire IA) | "ouvert" (cabinet
  // ouvert, hors plages Fruitycall) | "ferme"
  const etatCreneau = (date, minutes) => {
    if (findAbsence(toISODate(date), absences, minutes)) return "absence";
    const cle = dayKeyOf(date);
    if (dansPlages(hours[cle], minutes)) return "rdv";
    if (dansPlages(cabinetHours[cle], minutes)) return "ouvert";
    return "ferme";
  };

  const patientName = (apt) =>
    [apt.patient_prenom, apt.patient_nom].filter(Boolean).join(" ") || "Sans nom";

  // ---- Dialogue créneau ----

  const openScheduleDialog = (apt) =>
    setSlotDialog({
      apt,
      isNew: false,
      date: apt.scheduled_date || parseDateText(apt.date_rdv_souhaitee),
      time: apt.scheduled_time || parseTimeText(apt.heure_rdv_souhaitee),
      duration: apt.duration_minutes || 15,
    });

  const openCreateDialog = (date, minutes) =>
    setSlotDialog({
      isNew: true,
      date: toISODate(date),
      time: toHHMM(minutes),
      duration: 15,
      nom: "",
      telephone: "",
      motif: "",
    });

  const saveSlot = async () => {
    const { apt, date, time, duration, nom, telephone, motif, isNew } = slotDialog;
    if (!date || !time) {
      toast.error("Choisissez une date et une heure");
      return;
    }
    const absence = findAbsence(date, absences, toMinutes(time));
    if (absence) {
      toast.error(`Le cabinet est indiqué absent à ce moment : ${libelleAbsence(absence)}`);
      return;
    }
    if (isNew) {
      try {
        const response = await fetch(`${API_URL}/api/appointments`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            user_email: userEmail,
            nom,
            telephone,
            motif,
            scheduled_date: date,
            scheduled_time: time,
            duration_minutes: Number(duration) || 15,
            status: "confirme",
          }),
        });
        if (!response.ok) throw new Error();
        toast.success("Rendez-vous créé");
        setSlotDialog(null);
        loadAll(true);
      } catch {
        toast.error("Erreur lors de l'enregistrement");
      }
    } else {
      const ok = await patchAppointment(apt._id, {
        scheduled_date: date,
        scheduled_time: time,
        duration_minutes: Number(duration) || 15,
        status: "confirme",
      }, "Rendez-vous planifié");
      if (ok) setSlotDialog(null);
    }
  };

  const cancelAppointment = async (apt) => {
    const ok = await patchAppointment(apt._id, { status: "annule" }, "Rendez-vous annulé");
    if (ok) setSlotDialog(null);
  };

  // ---- Glisser-déposer (déplacement de RDV) ----

  const onBlockDragStart = (e, apt) => {
    const rect = e.currentTarget.getBoundingClientRect();
    dragRef.current = { apt, grabOffsetPx: e.clientY - rect.top };
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", apt._id);
  };

  const snapFromY = (clientY, columnEl, grabOffsetPx = 0) => {
    const rect = columnEl.getBoundingClientRect();
    const y = clientY - rect.top - grabOffsetPx;
    const raw = gridStart + (y / SLOT_HEIGHT) * SLOT_MINUTES;
    const snapped = Math.round(raw / SNAP_MINUTES) * SNAP_MINUTES;
    return Math.min(Math.max(snapped, gridStart), gridEnd - SNAP_MINUTES);
  };

  const onColumnDragOver = (e, date) => {
    if (!dragRef.current) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    const minutes = snapFromY(e.clientY, e.currentTarget, dragRef.current.grabOffsetPx);
    setDropPreview({
      iso: toISODate(date),
      minutes,
      duration: dragRef.current.apt.duration_minutes || 15,
    });
  };

  const onColumnDrop = async (e, date) => {
    e.preventDefault();
    const drag = dragRef.current;
    dragRef.current = null;
    setDropPreview(null);
    if (!drag) return;
    const iso = toISODate(date);
    const minutes = snapFromY(e.clientY, e.currentTarget, drag.grabOffsetPx);
    const absence = findAbsence(iso, absences, minutes);
    if (absence) {
      toast.error(`Le cabinet est indiqué absent à ce moment : ${libelleAbsence(absence)}`);
      return;
    }
    // Mise à jour optimiste pour un rendu immédiat
    setAppointments((prev) =>
      prev.map((a) =>
        a._id === drag.apt._id
          ? { ...a, scheduled_date: iso, scheduled_time: toHHMM(minutes) }
          : a
      )
    );
    await patchAppointment(drag.apt._id, {
      scheduled_date: iso,
      scheduled_time: toHHMM(minutes),
    }, "Rendez-vous déplacé");
  };

  const onDragEnd = () => {
    dragRef.current = null;
    setDropPreview(null);
  };

  // ---- Redimensionnement (durée) ----

  useEffect(() => {
    if (!resizing) return;
    const onMove = (e) => {
      const dy = e.clientY - resizing.startY;
      const delta = Math.round((dy / SLOT_HEIGHT) * SLOT_MINUTES / SNAP_MINUTES) * SNAP_MINUTES;
      const preview = Math.max(SNAP_MINUTES, resizing.baseDuration + delta);
      setResizing((r) => (r ? { ...r, preview } : r));
    };
    const onUp = async () => {
      const r = resizing;
      setResizing(null);
      if (r && r.preview && r.preview !== r.baseDuration) {
        setAppointments((prev) =>
          prev.map((a) => (a._id === r.apt._id ? { ...a, duration_minutes: r.preview } : a))
        );
        await patchAppointment(r.apt._id, { duration_minutes: r.preview }, "Durée modifiée");
      }
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, [resizing]);

  // ---- Horaires & absences (édition sur place) ----

  const saveHours = async () => {
    if (await persistSettings({ booking_hours: hours }, "Plages de rendez-vous enregistrées")) {
      setHoursDialog(false);
      setDispoModifiee(false);
    }
  };

  // --- Tracé des plages d'ouverture directement sur la grille ---

  const estOuvertPourJour = (dayKey, minutes) => {
    const jour = hours[dayKey];
    if (!jour?.open) return false;
    return jour.ranges.some(
      (r) => minutes >= toMinutes(r.start) && minutes < toMinutes(r.end)
    );
  };

  const appliquerTrace = (dayKey, debut, fin, ouvrir) => {
    setHours((prev) => {
      const jour = prev[dayKey] || { open: false, ranges: [] };
      const cases = rangesVersCases(jour.open ? jour.ranges : [], DISPO_START, DISPO_END);
      for (let m = debut; m < fin; m += RESOLUTION_PLAGES) {
        const i = (m - DISPO_START) / RESOLUTION_PLAGES;
        if (i >= 0 && i < cases.length) cases[i] = ouvrir;
      }
      const ranges = casesVersRanges(cases, DISPO_START);
      return {
        ...prev,
        [dayKey]: {
          open: ranges.length > 0,
          ranges: ranges.length > 0 ? ranges : [{ start: "09:00", end: "17:00" }],
        },
      };
    });
    setDispoModifiee(true);
  };

  // Fin du tracé au relâchement de la souris, même hors de la grille
  useEffect(() => {
    if (!tracage) return;
    const onUp = () => {
      const { dayKey, debut, fin, ouvrir } = tracage;
      appliquerTrace(dayKey, Math.min(debut, fin), Math.max(debut, fin) + SLOT_MINUTES, ouvrir);
      setTracage(null);
    };
    window.addEventListener("pointerup", onUp);
    return () => window.removeEventListener("pointerup", onUp);
  }, [tracage]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fin du tracé d'absence : dialogue pré-rempli avec la date et les heures
  useEffect(() => {
    if (!traceAbsence) return;
    const onUp = () => {
      const { iso, debut, fin } = traceAbsence;
      const bas = Math.min(debut, fin);
      const haut = Math.max(debut, fin) + SLOT_MINUTES;
      setTraceAbsence(null);
      setAbsenceForm({
        start_date: iso,
        end_date: iso,
        start_time: toHHMM(bas),
        end_time: toHHMM(haut),
        reason: "",
      });
      setAbsencesDialog(true);
    };
    window.addEventListener("pointerup", onUp);
    return () => window.removeEventListener("pointerup", onUp);
  }, [traceAbsence]);

  const dansTraceAbsence = (iso, minutes) =>
    !!traceAbsence &&
    traceAbsence.iso === iso &&
    minutes >= Math.min(traceAbsence.debut, traceAbsence.fin) &&
    minutes <= Math.max(traceAbsence.debut, traceAbsence.fin);

  const dansLeTrace = (dayKey, minutes) => {
    if (!tracage || tracage.dayKey !== dayKey) return null;
    const bas = Math.min(tracage.debut, tracage.fin);
    const haut = Math.max(tracage.debut, tracage.fin);
    return minutes >= bas && minutes <= haut ? tracage.ouvrir : null;
  };

  const addAbsence = async () => {
    if (!absenceForm.start_date || !absenceForm.end_date) {
      toast.error("Indiquez les dates de début et de fin");
      return;
    }
    if (absenceForm.end_date < absenceForm.start_date) {
      toast.error("La date de fin doit être après la date de début");
      return;
    }
    const { start_time, end_time } = absenceForm;
    if (!!start_time !== !!end_time) {
      toast.error("Indiquez l'heure de début et l'heure de fin, ou aucune pour la journée entière");
      return;
    }
    if (start_time && end_time <= start_time) {
      toast.error("L'heure de fin doit être après l'heure de début");
      return;
    }
    const entree = { id: Date.now().toString(), ...absenceForm };
    if (!start_time) {
      delete entree.start_time;
      delete entree.end_time;
    }
    const next = [...absences, entree].sort((a, b) =>
      (a.start_date + (a.start_time || "")).localeCompare(b.start_date + (b.start_time || ""))
    );
    if (await persistSettings({ absences: next }, "Absence enregistrée")) {
      setAbsences(next);
      setAbsenceForm({ start_date: "", end_date: "", start_time: "", end_time: "", reason: "" });
    }
  };

  const removeAbsence = async (id) => {
    const next = absences.filter((a) => a.id !== id);
    if (await persistSettings({ absences: next }, "Absence supprimée")) {
      setAbsences(next);
    }
  };

  const formatAbsenceDate = (iso) => {
    const [y, m, d] = (iso || "").split("-");
    return y ? `${d}/${m}/${y}` : iso;
  };

  // ---- Navigation ----

  const navigate = (dir) => {
    if (view === "jour") setAnchor(addDays(anchor, dir));
    else if (view === "semaine") setAnchor(addDays(anchor, dir * 7));
    else setAnchor(addMonths(anchor, dir));
  };

  const title = useMemo(() => {
    if (view === "jour")
      return anchor.toLocaleDateString("fr-FR", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    if (view === "semaine") {
      const start = mondayOf(anchor);
      return `Semaine du ${start.toLocaleDateString("fr-FR", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })}`;
    }
    return anchor.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
  }, [view, anchor]);

  // ---- Rendu de la grille horaire (vues jour et semaine) ----

  const renderTimeGrid = (dates) => (
    <div className="overflow-x-auto border rounded-lg">
      <div style={{ minWidth: dates.length > 1 ? 900 : 320 }}>
        {/* En-têtes */}
        <div className="grid" style={{ gridTemplateColumns: `56px repeat(${dates.length}, 1fr)` }}>
          <div />
          {dates.map((date) => {
            const iso = toISODate(date);
            const absencesJour = absencesDuJour(iso, absences);
            return (
              <div
                key={iso}
                className={`text-center py-2 text-sm font-medium border-l ${
                  iso === todayISO ? "bg-primary/10" : ""
                }`}
              >
                {date.toLocaleDateString("fr-FR", { weekday: "long" })}
                <div className="text-xs text-muted-foreground">
                  {date.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" })}
                </div>
                {absencesJour.map((a) => (
                  <div
                    key={a.id}
                    className="text-xs text-red-600 font-normal truncate px-1"
                    title={libelleAbsence(a)}
                  >
                    {libelleAbsence(a)}
                  </div>
                ))}
              </div>
            );
          })}
        </div>

        {/* Grille */}
        <div className="grid border-t" style={{ gridTemplateColumns: `56px repeat(${dates.length}, 1fr)` }}>
          {/* Colonne des heures */}
          <div className="relative" style={{ height: nSlots * SLOT_HEIGHT }}>
            {Array.from({ length: nSlots }).map((_, s) => {
              const minutes = gridStart + s * SLOT_MINUTES;
              return (
                <div
                  key={s}
                  className="absolute right-1 text-xs text-muted-foreground"
                  style={{ top: s * SLOT_HEIGHT - 7 }}
                >
                  {minutes % 60 === 0 && s > 0 ? toHHMM(minutes) : ""}
                </div>
              );
            })}
          </div>

          {/* Colonnes des jours */}
          {dates.map((date) => {
            const iso = toISODate(date);
            const absence = findAbsence(iso, absences);
            const dayAppointments = scheduled.filter((a) => a.scheduled_date === iso);
            return (
              <div
                key={iso}
                className="relative border-l"
                style={{ height: nSlots * SLOT_HEIGHT }}
                onDragOver={(e) => onColumnDragOver(e, date)}
                onDrop={(e) => onColumnDrop(e, date)}
              >
                {/* Créneaux */}
                {Array.from({ length: nSlots }).map((_, s) => {
                  const minutes = gridStart + s * SLOT_MINUTES;
                  const dayKey = dayKeyOf(date);

                  if (modeAbsence) {
                    const etat = etatCreneau(date, minutes);
                    const selectionne = dansTraceAbsence(iso, minutes);
                    return (
                      <div
                        key={s}
                        onPointerDown={(e) => {
                          e.preventDefault();
                          setTraceAbsence({ iso, debut: minutes, fin: minutes });
                        }}
                        onPointerEnter={() =>
                          traceAbsence &&
                          traceAbsence.iso === iso &&
                          setTraceAbsence({ ...traceAbsence, fin: minutes })
                        }
                        className={`border-t select-none cursor-crosshair ${
                          classeLigne(minutes)
                        } ${selectionne ? "bg-red-300/70" : ZONE_STYLES[etat]}`}
                        style={{
                          height: SLOT_HEIGHT,
                          ...(!selectionne && etat === "absence" ? HACHURES_ABSENCE : {}),
                        }}
                      />
                    );
                  }

                  if (modeDispo) {
                    // Mode disponibilités : on dessine les plages d'ouverture
                    const apercu = dansLeTrace(dayKey, minutes);
                    const ouvert =
                      apercu !== null ? apercu : estOuvertPourJour(dayKey, minutes);
                    return (
                      <div
                        key={s}
                        onPointerDown={(e) => {
                          e.preventDefault();
                          setTracage({
                            dayKey,
                            debut: minutes,
                            fin: minutes,
                            ouvrir: !estOuvertPourJour(dayKey, minutes),
                          });
                        }}
                        onPointerEnter={() =>
                          tracage &&
                          tracage.dayKey === dayKey &&
                          setTracage({ ...tracage, fin: minutes })
                        }
                        className={`border-t select-none cursor-crosshair ${
                          classeLigne(minutes)
                        } ${ouvert ? "bg-primary/25" : "bg-muted/40"}`}
                        style={{ height: SLOT_HEIGHT }}
                      />
                    );
                  }

                  const etat = etatCreneau(date, minutes);
                  const cliquable = etat === "rdv" || etat === "ouvert";
                  return (
                    <div
                      key={s}
                      onClick={cliquable ? () => openCreateDialog(date, minutes) : undefined}
                      title={
                        etat === "rdv"
                          ? "Plage de rendez-vous : proposée par la secrétaire IA"
                          : etat === "ouvert"
                          ? "Cabinet ouvert, hors plages proposées par la secrétaire IA"
                          : etat === "absence"
                          ? "Absence"
                          : "Cabinet fermé"
                      }
                      className={`border-t ${
                        classeLigne(minutes)
                      } ${ZONE_STYLES[etat]}`}
                      style={{
                        height: SLOT_HEIGHT,
                        ...(etat === "absence" ? HACHURES_ABSENCE : {}),
                      }}
                    />
                  );
                })}

                {/* Aperçu de dépôt pendant un glisser-déposer */}
                {dropPreview && dropPreview.iso === iso && (
                  <div
                    className="absolute left-0.5 right-0.5 rounded border-2 border-dashed border-primary/60 bg-primary/10 pointer-events-none"
                    style={{
                      top: ((dropPreview.minutes - gridStart) / SLOT_MINUTES) * SLOT_HEIGHT,
                      height: (dropPreview.duration / SLOT_MINUTES) * SLOT_HEIGHT - 2,
                    }}
                  />
                )}

                {/* Rendez-vous (masqués pendant l'édition des disponibilités) */}
                {!modeDispo && dayAppointments.map((apt) => {
                  const inerte = modeAbsence;
                  const startMin = toMinutes(apt.scheduled_time);
                  if (startMin < gridStart || startMin >= gridEnd) return null;
                  const duration =
                    resizing?.apt._id === apt._id && resizing.preview
                      ? resizing.preview
                      : apt.duration_minutes || 15;
                  const top = ((startMin - gridStart) / SLOT_MINUTES) * SLOT_HEIGHT;
                  const height = Math.max(20, (duration / SLOT_MINUTES) * SLOT_HEIGHT - 2);
                  const confirmed = apt.status === "confirme";
                  return (
                    <div
                      key={apt._id}
                      draggable={!resizing && !inerte}
                      onDragStart={(e) => onBlockDragStart(e, apt)}
                      onDragEnd={onDragEnd}
                      onClick={inerte ? undefined : () => openScheduleDialog(apt)}
                      className={`absolute left-0.5 right-0.5 rounded px-1.5 py-0.5 text-left text-xs overflow-hidden border select-none ${
                        inerte ? "pointer-events-none opacity-50" : "cursor-grab active:cursor-grabbing"
                      } ${
                        confirmed
                          ? "bg-green-100 border-green-300 text-green-900 hover:bg-green-200"
                          : "bg-amber-100 border-amber-300 text-amber-900 hover:bg-amber-200"
                      }`}
                      style={{ top, height }}
                      title={`${apt.scheduled_time} — ${patientName(apt)}${apt.motif ? " : " + apt.motif : ""} (glisser pour déplacer)`}
                    >
                      <span className="font-medium">
                        {resizing?.apt._id === apt._id && resizing.preview
                          ? `${apt.scheduled_time} · ${duration} min`
                          : apt.scheduled_time}
                      </span>{" "}
                      {patientName(apt)}
                      {height >= 40 && apt.motif && (
                        <div className="truncate opacity-75">{apt.motif}</div>
                      )}
                      {/* Poignée de redimensionnement */}
                      <div
                        draggable={false}
                        onPointerDown={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          setResizing({
                            apt,
                            startY: e.clientY,
                            baseDuration: apt.duration_minutes || 15,
                            preview: null,
                          });
                        }}
                        onClick={(e) => e.stopPropagation()}
                        className="absolute bottom-0 left-0 right-0 h-1.5 cursor-ns-resize hover:bg-black/10"
                      />
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  // ---- Vue mois ----

  const renderMonth = () => {
    const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const start = mondayOf(first);
    const cells = Array.from({ length: 42 }, (_, i) => addDays(start, i));
    return (
      <div className="border rounded-lg overflow-hidden">
        <div className="grid grid-cols-7 text-center text-xs font-medium text-muted-foreground border-b">
          {AGENDA_DAYS.map((d) => (
            <div key={d.key} className="py-1.5">{d.label}</div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {cells.map((date, i) => {
            const iso = toISODate(date);
            const inMonth = date.getMonth() === anchor.getMonth();
            const absence = findAbsence(iso, absences);
            const dayAppointments = scheduled
              .filter((a) => a.scheduled_date === iso)
              .sort((a, b) => (a.scheduled_time || "").localeCompare(b.scheduled_time || ""));
            const cle = dayKeyOf(date);
            const zoneJour = absence
              ? "absence"
              : hours[cle]?.open
              ? "rdv"
              : cabinetHours[cle]?.open
              ? "ouvert"
              : "ferme";
            return (
              <div
                key={i}
                onClick={() => {
                  setAnchor(date);
                  changeView("jour");
                }}
                className={`min-h-24 border-b border-r p-1 cursor-pointer transition-colors ${
                  !inMonth ? "opacity-40" : ""
                } ${ZONE_STYLES[zoneJour]} ${iso === todayISO ? "ring-2 ring-inset ring-primary/40" : ""}`}
                style={zoneJour === "absence" ? HACHURES_ABSENCE : undefined}
              >
                <div className="flex items-center justify-between text-xs">
                  <span className={iso === todayISO ? "font-bold" : ""}>{date.getDate()}</span>
                  {absencesDuJour(iso, absences).length > 0 && (
                    <span className="text-red-600 truncate max-w-[70%]"
                      title={absencesDuJour(iso, absences).map(libelleAbsence).join(" · ")}>
                      {absencesDuJour(iso, absences).map(libelleAbsence).join(" · ")}
                    </span>
                  )}
                </div>
                <div className="mt-0.5 space-y-0.5">
                  {dayAppointments.slice(0, 3).map((apt) => (
                    <div
                      key={apt._id}
                      onClick={(e) => {
                        e.stopPropagation();
                        openScheduleDialog(apt);
                      }}
                      className={`truncate rounded px-1 text-xs border ${
                        apt.status === "confirme"
                          ? "bg-green-100 border-green-300 text-green-900"
                          : "bg-amber-100 border-amber-300 text-amber-900"
                      }`}
                    >
                      {apt.scheduled_time} {patientName(apt)}
                    </div>
                  ))}
                  {dayAppointments.length > 3 && (
                    <div className="text-xs text-muted-foreground px-1">
                      +{dayAppointments.length - 3} autres
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const datesToRender =
    view === "jour"
      ? [anchor]
      : AGENDA_DAYS.map((_, i) => addDays(mondayOf(anchor), i));

  return (
    <div className="min-h-screen bg-background">
      <AppHeader />
      <main className="container-custom py-8">
        <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Calendar className="h-6 w-6" />
              Agenda
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              Cliquez sur un créneau libre pour créer un rendez-vous, glissez-déposez un
              rendez-vous pour le déplacer, étirez son bord inférieur pour changer sa durée.
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant={modeDispo ? "default" : "outline"}
              size="sm"
              className="gap-2"
              onClick={() => {
                if (modeDispo && dispoModifiee) {
                  toast.error("Enregistrez ou annulez vos modifications d'abord");
                  return;
                }
                if (modeAbsence) {
                  toast.error("Terminez d'abord le tracé d'absence");
                  return;
                }
                setModeDispo(!modeDispo);
                if (view === "mois") changeView("semaine");
              }}
            >
              <Clock className="h-4 w-4" />
              {modeDispo ? "Terminer" : "Plages de rendez-vous"}
            </Button>
            <Button
              variant={modeAbsence ? "destructive" : "outline"}
              size="sm"
              className="gap-2"
              onClick={() => {
                if (modeDispo) {
                  toast.error("Terminez d'abord l'édition des plages de rendez-vous");
                  return;
                }
                setModeAbsence(!modeAbsence);
                if (view === "mois") changeView("semaine");
              }}
            >
              <CalendarOff className="h-4 w-4" />
              {modeAbsence ? "Terminer" : "Tracer une absence"}
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="gap-2"
              onClick={() => setAbsencesDialog(true)}
            >
              <CalendarOff className="h-4 w-4" />
              Absences
            </Button>
          </div>
        </div>

        {/* Bandeau du mode tracé d'absence */}
        {modeAbsence && (
          <Card className="mb-4 border-red-300 bg-red-50/60">
            <CardContent className="p-3 text-sm">
              <span className="font-semibold">Tracer une absence.</span> Cliquez-glissez sur une
              colonne, du début à la fin de votre absence : une fenêtre s'ouvre, déjà remplie avec
              la date et les heures, pour indiquer le motif et confirmer. Ces créneaux ne seront
              plus proposés aux patients par la secrétaire IA.
            </CardContent>
          </Card>
        )}

        {/* Bandeau du mode disponibilités */}
        {modeDispo && (
          <Card className="mb-4 border-primary/40 bg-primary/5">
            <CardContent className="p-3 flex items-center justify-between gap-3 flex-wrap">
              <p className="text-sm">
                <span className="font-semibold">Plages de rendez-vous.</span> Cliquez-glissez
                sur une colonne pour ouvrir ou fermer une plage. Les zones colorées sont les
                heures que vous confiez à la secrétaire IA :{" "}
                <span className="font-medium">elle ne proposera aux patients que ces
                créneaux-là</span>. Indépendant des horaires d'ouverture du cabinet, réglés
                dans les Paramètres. Les plages valent pour tous les
                {" "}{view === "jour" ? "jours identiques" : "mêmes jours de la semaine"}.
              </p>
              <div className="flex gap-2 shrink-0">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!dispoModifiee}
                  onClick={() => {
                    loadAll(true);
                    setDispoModifiee(false);
                    toast.success("Modifications annulées");
                  }}
                >
                  Annuler
                </Button>
                <Button size="sm" disabled={!dispoModifiee} onClick={saveHours}>
                  Enregistrer
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Demandes à planifier */}
        {!modeDispo && pending.length > 0 && (
          <section className="mb-6">
            <h2 className="text-lg font-semibold mb-3">
              Demandes à planifier ({pending.length})
            </h2>
            {pending.map((apt) => (
              <Card key={apt._id} className="mb-2">
                <CardContent className="p-3 flex items-center justify-between flex-wrap gap-2">
                  <div className="text-sm min-w-0">
                    <span className="font-semibold">{patientName(apt)}</span>
                    {apt.deja_suivi === false && (
                      <span className="ml-2 inline-block rounded bg-amber-100 px-1.5 text-xs font-medium text-amber-800 align-middle">
                        Nouveau patient
                      </span>
                    )}
                    {apt.motif && <span className="text-muted-foreground"> — {apt.motif}</span>}
                    <div className="text-muted-foreground flex items-center gap-3 flex-wrap mt-0.5">
                      {(apt.date_rdv_souhaitee || apt.heure_rdv_souhaitee) && (
                        <span>
                          Souhait : {apt.date_rdv_souhaitee || "?"}
                          {apt.heure_rdv_souhaitee ? ` à ${apt.heure_rdv_souhaitee}` : ""}
                        </span>
                      )}
                      {apt.telephone && (
                        <a
                          href={`tel:${apt.telephone.replace(/\s/g, "")}`}
                          className="flex items-center gap-1 hover:text-foreground"
                        >
                          <Phone className="h-3 w-3" />
                          {apt.telephone}
                        </a>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button size="sm" onClick={() => openScheduleDialog(apt)}>
                      Planifier
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-destructive hover:text-destructive"
                      onClick={() => cancelAppointment(apt)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </section>
        )}

        {/* Barre de navigation + choix de vue */}
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate(-1)}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const d = new Date();
                d.setHours(0, 0, 0, 0);
                setAnchor(d);
              }}
            >
              Aujourd'hui
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate(1)}>
              <ChevronRight className="h-4 w-4" />
            </Button>
            <span className="font-medium text-sm ml-2 capitalize">{title}</span>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {/* Zoom : précision du clic sur la grille (sans objet en vue mois) */}
            {view !== "mois" && (
              <div
                className="flex items-center rounded-lg border overflow-hidden"
                title="Précision du clic sur l'agenda"
              >
                <span className="flex items-center px-2 text-muted-foreground" aria-hidden="true">
                  <ZoomIn className="h-4 w-4" />
                </span>
                {ZOOMS.map((z) => (
                  <button
                    key={z.pas}
                    onClick={() => changeZoom(z.pas)}
                    aria-pressed={zoom === z.pas}
                    className={`px-2.5 py-1.5 text-sm ${
                      zoom === z.pas
                        ? "bg-primary text-primary-foreground"
                        : "hover:bg-muted text-muted-foreground"
                    }`}
                  >
                    {z.label}
                  </button>
                ))}
              </div>
            )}
            <div className="flex rounded-lg border overflow-hidden">
              {VIEWS.map((v) => (
                <button
                  key={v.key}
                  onClick={() => changeView(v.key)}
                  className={`px-3 py-1.5 text-sm ${
                    view === v.key
                      ? "bg-primary text-primary-foreground"
                      : "hover:bg-muted text-muted-foreground"
                  }`}
                >
                  {v.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {loading ? (
          <p className="text-muted-foreground">Chargement…</p>
        ) : view === "mois" ? (
          renderMonth()
        ) : (
          renderTimeGrid(datesToRender)
        )}

        <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-muted-foreground">
          <span className="font-medium text-foreground">Zones :</span>
          <span className="flex items-center gap-1.5">
            <span className="inline-block h-3 w-3 rounded border border-emerald-300 bg-emerald-50" />
            Plages de rendez-vous (proposées par la secrétaire IA)
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-block h-3 w-3 rounded border border-sky-300 bg-sky-50" />
            Cabinet ouvert (hors secrétaire IA)
          </span>
          <span className="flex items-center gap-1.5">
            <span
              className="inline-block h-3 w-3 rounded border border-red-300 bg-red-100/70"
              style={HACHURES_ABSENCE}
            />
            Absence
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-block h-3 w-3 rounded border bg-muted" />
            Fermé
          </span>
          <span className="ml-2 font-medium text-foreground">Rendez-vous :</span>
          <span className="flex items-center gap-1.5">
            <span className="inline-block h-3 w-3 rounded border border-green-300 bg-green-100" />
            Confirmé
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-block h-3 w-3 rounded border border-amber-300 bg-amber-100" />
            À confirmer
          </span>
        </div>
      </main>

      {/* Dialogue planification / création */}
      <Dialog open={!!slotDialog} onOpenChange={(open) => !open && setSlotDialog(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {slotDialog?.isNew
                ? "Nouveau rendez-vous"
                : `Planifier — ${slotDialog?.apt ? patientName(slotDialog.apt) : ""}`}
            </DialogTitle>
          </DialogHeader>
          {slotDialog && (
            <div className="space-y-3">
              {slotDialog.isNew ? (
                <>
                  <div>
                    <Label htmlFor="rdv-nom">Patient</Label>
                    <Input
                      id="rdv-nom"
                      value={slotDialog.nom}
                      onChange={(e) => setSlotDialog({ ...slotDialog, nom: e.target.value })}
                      placeholder="Nom et prénom"
                    />
                  </div>
                  <div>
                    <Label htmlFor="rdv-tel">Téléphone</Label>
                    <Input
                      id="rdv-tel"
                      value={slotDialog.telephone}
                      onChange={(e) => setSlotDialog({ ...slotDialog, telephone: e.target.value })}
                      placeholder="06 …"
                    />
                  </div>
                  <div>
                    <Label htmlFor="rdv-motif">Motif</Label>
                    <Input
                      id="rdv-motif"
                      value={slotDialog.motif}
                      onChange={(e) => setSlotDialog({ ...slotDialog, motif: e.target.value })}
                      placeholder="Consultation, renouvellement…"
                    />
                  </div>
                </>
              ) : (
                slotDialog.apt && (
                  <div className="text-sm text-muted-foreground space-y-1">
                    {slotDialog.apt.motif && <p>Motif : {slotDialog.apt.motif}</p>}
                    {(slotDialog.apt.date_rdv_souhaitee || slotDialog.apt.heure_rdv_souhaitee) && (
                      <p>
                        Souhait du patient : {slotDialog.apt.date_rdv_souhaitee || "?"}
                        {slotDialog.apt.heure_rdv_souhaitee
                          ? ` à ${slotDialog.apt.heure_rdv_souhaitee}`
                          : ""}
                      </p>
                    )}
                    {slotDialog.apt.telephone && <p>Téléphone : {slotDialog.apt.telephone}</p>}
                    {slotDialog.apt.date_naissance && (
                      <p>Né(e) le {slotDialog.apt.date_naissance}</p>
                    )}
                  </div>
                )
              )}
              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-1">
                  <Label htmlFor="rdv-date">Date</Label>
                  <Input
                    id="rdv-date"
                    type="date"
                    value={slotDialog.date}
                    onChange={(e) => setSlotDialog({ ...slotDialog, date: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="rdv-heure">Heure</Label>
                  <Input
                    id="rdv-heure"
                    type="time"
                    step="300"
                    value={slotDialog.time}
                    onChange={(e) => setSlotDialog({ ...slotDialog, time: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="rdv-duree">Durée (min)</Label>
                  <Input
                    id="rdv-duree"
                    type="number"
                    min="5"
                    step="5"
                    value={slotDialog.duration}
                    onChange={(e) => setSlotDialog({ ...slotDialog, duration: e.target.value })}
                  />
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            {slotDialog && !slotDialog.isNew && (
              <Button
                variant="outline"
                className="text-destructive hover:text-destructive mr-auto"
                onClick={() => cancelAppointment(slotDialog.apt)}
              >
                Annuler le RDV
              </Button>
            )}
            <Button variant="outline" onClick={() => setSlotDialog(null)}>
              Fermer
            </Button>
            <Button onClick={saveSlot}>Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialogue absences */}
      <Dialog open={absencesDialog} onOpenChange={setAbsencesDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Absences du cabinet</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {absences.length === 0 ? (
              <p className="text-sm text-muted-foreground">Aucune absence programmée.</p>
            ) : (
              <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                {absences.map((absence) => (
                  <div
                    key={absence.id}
                    className="flex items-center justify-between rounded-lg border p-2.5 text-sm"
                  >
                    <div>
                      <span className="font-medium">
                        {absence.start_date === absence.end_date
                          ? `Le ${formatAbsenceDate(absence.start_date)}`
                          : `Du ${formatAbsenceDate(absence.start_date)} au ${formatAbsenceDate(absence.end_date)}`}
                        {absence.start_time
                          ? `, de ${absence.start_time} à ${absence.end_time}`
                          : ", journée entière"}
                      </span>
                      {absence.reason && (
                        <span className="text-muted-foreground"> — {absence.reason}</span>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 text-destructive"
                      onClick={() => removeAbsence(absence.id)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            <div className="rounded-lg border p-3 space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label htmlFor="agenda-absence-start">Du</Label>
                  <Input
                    id="agenda-absence-start"
                    type="date"
                    value={absenceForm.start_date}
                    onChange={(e) =>
                      setAbsenceForm({ ...absenceForm, start_date: e.target.value })
                    }
                  />
                </div>
                <div>
                  <Label htmlFor="agenda-absence-end">Au (inclus)</Label>
                  <Input
                    id="agenda-absence-end"
                    type="date"
                    value={absenceForm.end_date}
                    onChange={(e) =>
                      setAbsenceForm({ ...absenceForm, end_date: e.target.value })
                    }
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label htmlFor="agenda-absence-debut">De (optionnel)</Label>
                  <Input
                    id="agenda-absence-debut"
                    type="time"
                    step="900"
                    value={absenceForm.start_time}
                    onChange={(e) => setAbsenceForm({ ...absenceForm, start_time: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="agenda-absence-fin">À (optionnel)</Label>
                  <Input
                    id="agenda-absence-fin"
                    type="time"
                    step="900"
                    value={absenceForm.end_time}
                    onChange={(e) => setAbsenceForm({ ...absenceForm, end_time: e.target.value })}
                  />
                </div>
              </div>
              <p className="text-xs text-muted-foreground -mt-1">
                Laissez les heures vides pour une absence sur la journée entière.
              </p>
              <div>
                <Label htmlFor="agenda-absence-reason">Motif (optionnel)</Label>
                <Input
                  id="agenda-absence-reason"
                  value={absenceForm.reason}
                  onChange={(e) => setAbsenceForm({ ...absenceForm, reason: e.target.value })}
                  placeholder="Congés, formation…"
                />
              </div>
              <Button onClick={addAbsence} className="w-full gap-1">
                <Plus className="h-4 w-4" />
                Ajouter l'absence
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAbsencesDialog(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Agenda;
